import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { FileText, Copy, Check, RefreshCw, AlertCircle } from 'lucide-react'
import AgentHeader from '../../components/AgentHeader'
import { cn } from '../../utils/designSystem'
import LoadingSkeleton from '../../components/LoadingSkeleton'
import { formatFileSize } from './pdfUtils'

// Function to get user-friendly model name
const getModelDisplayName = (mode) => {
  const modelNames = {
    'standard': 'Standard Mode',
    'thinking': 'Thinking Mode',
    'genius': 'Genius Mode',
    'clever': 'Clever Mode',
    'smart': 'Smart Mode',
    'agentpro': 'AgentPro Mode',
    'botpro': 'AgentPro Mode' // backward compatibility
  }
  return modelNames[mode] || mode || 'AI Model'
}

const PDFResultPage = ({ 
  query, 
  analysisResult, 
  isLoading, 
  error, 
  isCompleted, 
  pdfFile, 
  onBack, 
  onReanalyze 
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopyResult = async () => {
    if (!analysisResult?.analysis) return
    
    try {
      // Convert markdown to HTML for rich text copying
      const markdownToHtml = (markdown) => {
        return markdown
          .replace(/^### (.*$)/gim, '<h3>$1</h3>')
          .replace(/^## (.*$)/gim, '<h2>$1</h2>')
          .replace(/^# (.*$)/gim, '<h1>$1</h1>')
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/`(.*?)`/g, '<code>$1</code>')
          .replace(/^\* (.*$)/gim, '<li>$1</li>')
          .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
          .replace(/\n\n/g, '</p><p>')
          .replace(/^(.*)$/gm, (match, p1) => {
            if (p1.startsWith('<h') || p1.startsWith('<li') || p1.trim() === '') return p1
            return `<p>${p1}</p>`
          })
          .replace(/<p><\/p>/g, '')
          .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
      }
      
      const htmlContent = markdownToHtml(analysisResult.analysis)
      
      // Try to copy as rich text (HTML) first
      if (navigator.clipboard && window.ClipboardItem) {
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' })
        const textBlob = new Blob([analysisResult.analysis], { type: 'text/plain' })
        
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        })
        
        await navigator.clipboard.write([clipboardItem])
      } else {
        // Fallback to plain text
        await navigator.clipboard.writeText(analysisResult.analysis)
      }
      
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
      // Fallback to plain text if rich text copying fails
      try {
        await navigator.clipboard.writeText(analysisResult.analysis)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (fallbackErr) {
        console.error('Failed to copy plain text:', fallbackErr)
      }
    }
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-primary via-bg-secondary to-bg-tertiary overflow-x-hidden w-full max-w-full mobile-safe">
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={FileText}
        title="PDF Agent"
        query="Analisis dokumen PDF dengan AI"
        iconColor="text-white"
        iconBgColor="bg-gradient-to-br from-red-500 to-orange-500"
        actions={isCompleted ? [
          {
            onClick: handleCopyResult,
            icon: copied ? Check : Copy,
            label: copied ? 'Tersalin' : 'Salin',
            hideOnMobile: false,
            className: "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-bg-tertiary hover:bg-bg-secondary text-text-secondary hover:text-text-primary rounded-lg transition-colors text-xs sm:text-sm"
          },
          {
            onClick: onReanalyze,
            icon: RefreshCw,
            label: 'Analisis Ulang',
            hideOnMobile: false,
            className: "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white rounded-lg transition-colors text-xs sm:text-sm"
          }
        ] : []}
      />

      {/* Content */}
      <div className="w-full max-w-full mx-auto px-0 sm:px-4 py-3 sm:py-8 box-border mobile-safe">
        <div className="w-full max-w-full flex flex-col lg:grid lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-8 box-border mobile-safe">
          {/* PDF Info Sidebar */}
          <div className="w-full max-w-full lg:col-span-1 order-2 lg:order-1 box-border mobile-safe">
            <div className="w-full max-w-full bg-bg-secondary/50 backdrop-blur-sm rounded-none sm:rounded-xl border-0 sm:border border-border-primary p-3 sm:p-6 lg:sticky lg:top-24 box-border overflow-hidden mobile-safe">
              <h3 className="text-sm sm:text-lg font-semibold text-text-primary mb-2 sm:mb-4">Informasi PDF</h3>
              
              {pdfFile && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-bg-tertiary/50 rounded-lg">
                    <FileText className="w-5 h-5 sm:w-8 sm:h-8 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-text-primary truncate">
                        {pdfFile.name}
                      </p>
                      <p className="text-xs text-text-tertiary">
                        {formatFileSize(pdfFile.size)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm gap-1 sm:gap-0">
                      <span className="text-text-secondary">Nama File:</span>
                      <span className="text-text-primary font-medium break-words sm:truncate sm:ml-2 mobile-text">{pdfFile.name}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm gap-1 sm:gap-0">
                      <span className="text-text-secondary">Ukuran:</span>
                      <span className="text-text-primary font-medium">{formatFileSize(pdfFile.size)}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between text-xs sm:text-sm gap-1 sm:gap-0">
                      <span className="text-text-secondary">Tipe:</span>
                      <span className="text-text-primary font-medium">PDF</span>
                    </div>
                  </div>
                  
                  {analysisResult && (
                    <div className="pt-3 sm:pt-4 border-t border-border-primary space-y-2">
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-text-secondary">Teks Diekstrak:</span>
                        <span className="text-text-primary font-medium">
                          {analysisResult.extractedTextLength?.toLocaleString()} karakter
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-text-secondary">Model AI:</span>
                        <span className="text-text-primary font-medium truncate ml-2 mobile-text">{getModelDisplayName(analysisResult.mode)}</span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-text-secondary">Dianalisis:</span>
                        <span className="text-text-primary font-medium text-right ml-2">
                          {new Date(analysisResult.generatedAt).toLocaleString('id-ID')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Query Display */}
              <div className="mt-3 sm:mt-6 pt-3 sm:pt-6 border-t border-border-primary">
                <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-2">Pertanyaan Anda:</h4>
                <div className="p-2 sm:p-3 bg-bg-tertiary/50 rounded-lg">
                  <p className="text-xs sm:text-sm text-text-secondary break-words mobile-text">{query}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full max-w-full lg:col-span-2 order-1 lg:order-2 box-border mobile-safe">
            <div className="w-full max-w-full bg-bg-secondary/50 backdrop-blur-sm rounded-none sm:rounded-xl border-0 sm:border border-border-primary p-3 sm:p-6 box-border overflow-hidden mobile-safe">
              <h2 className="text-base sm:text-xl font-semibold text-text-primary mb-3 sm:mb-6">Hasil Analisis</h2>
              
              {/* Loading State */}
              {isLoading && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-blue-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    <span className="text-sm font-medium">Menganalisis PDF...</span>
                  </div>
                  <LoadingSkeleton lines={8} />
                </div>
              )}
              
              {/* Error State */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-red-400 mb-1">Terjadi Kesalahan</h3>
                      <p className="text-sm text-red-300">{error}</p>
                      <button
                        onClick={onReanalyze}
                        className="mt-3 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
                      >
                        Coba Lagi
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Success State */}
              {isCompleted && analysisResult && (
                <div className="space-y-3 sm:space-y-6">
                    <div className="w-full max-w-full overflow-x-hidden prevent-overflow force-word-wrap mobile-safe">
                      <div className="w-full bg-bg-tertiary/30 rounded-lg p-2 sm:p-6 border border-border-primary overflow-x-hidden break-words force-word-wrap prevent-overflow mobile-safe">
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-lg sm:text-2xl font-bold text-text-primary mb-2 sm:mb-4 mt-3 sm:mt-6 break-words mobile-text" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-base sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3 mt-3 sm:mt-5 break-words mobile-text" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-sm sm:text-lg font-medium text-text-primary mb-1 sm:mb-2 mt-2 sm:mt-4 break-words mobile-text" {...props} />,
                          h4: ({node, ...props}) => <h4 className="text-sm sm:text-base font-medium text-text-primary mb-1 sm:mb-2 mt-2 sm:mt-3 break-words mobile-text" {...props} />,
                          p: ({node, ...props}) => <p className="mb-2 sm:mb-4 text-text-secondary leading-relaxed text-xs sm:text-base break-words force-word-wrap prevent-overflow mobile-text" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc ml-3 sm:ml-6 mb-2 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-xs sm:text-base" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal ml-3 sm:ml-6 mb-2 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-xs sm:text-base" {...props} />,
                          li: ({node, ...props}) => <li className="mb-1 leading-relaxed break-words force-word-wrap prevent-overflow mobile-text" {...props} />,
                          blockquote: ({node, ...props}) => <blockquote className="border-l-2 sm:border-l-4 border-emerald-500 pl-2 sm:pl-4 italic text-text-tertiary mb-2 sm:mb-4 text-xs sm:text-base break-words" {...props} />,
                          code: ({node, inline, ...props}) => 
                            inline 
                              ? <code className="bg-bg-tertiary text-emerald-400 px-1 py-0.5 rounded text-xs break-all" {...props} />
                              : <code className="block bg-bg-tertiary text-text-secondary p-2 sm:p-4 rounded-lg mb-2 sm:mb-4 overflow-x-auto text-xs sm:text-sm break-all" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold text-text-primary break-words force-word-wrap prevent-overflow mobile-text" {...props} />,
                          em: ({node, ...props}) => <em className="italic text-text-secondary break-words" {...props} />,
                          a: ({node, ...props}) => <a className="text-emerald-400 hover:text-emerald-300 underline break-all" {...props} />,
                          table: ({node, ...props}) => (
                            <div className="w-full overflow-x-auto mb-3 sm:mb-6 -mx-3 sm:mx-0">
                              <table className="w-full min-w-0 border-collapse border border-border-secondary rounded-lg text-xs" {...props} />
                            </div>
                          ),
                          th: ({node, ...props}) => <th className="border border-border-secondary bg-bg-tertiary p-1 sm:p-3 text-left font-semibold text-text-primary text-xs break-words" {...props} />,
                          td: ({node, ...props}) => <td className="border border-border-secondary p-1 sm:p-3 text-text-secondary text-xs break-words" {...props} />
                        }}
                      >
                        {analysisResult.analysis}
                      </ReactMarkdown>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="w-full flex flex-col sm:flex-row flex-wrap gap-2 pt-3 sm:pt-4 border-t border-border-primary mobile-safe">
                    <button
                      onClick={handleCopyResult}
                      className={cn(
                        "flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm",
                        copied
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-bg-tertiary hover:bg-bg-secondary text-text-secondary hover:text-text-primary border border-border-primary"
                      )}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span>{copied ? 'Tersalin!' : 'Salin Hasil'}</span>
                    </button>
                    

                    <button
                      onClick={onReanalyze}
                      className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white rounded-lg transition-colors text-sm"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Analisis Ulang</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

PDFResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  analysisResult: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  pdfFile: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onReanalyze: PropTypes.func.isRequired
}

export default PDFResultPage